// src/rms/data/glService.js
// ============================================================================
// Posting to the general ledger
// ============================================================================
// One place that knows how a document becomes a balanced set of entries. See
// 20260820160000_rms_general_ledger.sql for why the ledger is double-entry,
// accrual, and append-only; this file is the other half of that decision and
// the two have to be read together.
//
// Every function here builds a batch that sums to zero and writes it whole. A
// deferred constraint trigger refuses anything that does not balance, so a bug
// in this file is a failed write rather than a quietly wrong report -- which is
// the entire reason for choosing double entry over a list of amounts.
//
// Posting never throws into the caller's path. A failed ledger write must not
// prevent a customer being invoiced or a payment being taken: the document is
// the operational record and the ledger is derived from it. Failures are
// returned, and the document remains postable later, because source_table and
// source_id say exactly what has and has not been posted.
// ============================================================================

import db from "./index";
import { createSuccessResponse, createErrorResponse } from "../utils/response";

export const ACCOUNTS = {
  UNDEPOSITED: "1000",
  RECEIVABLE: "1100",
  DEPOSITS_HELD: "2100",
  TAX_PAYABLE: "2200",
  RENTAL_REVENUE: "4000",
  ADDON_REVENUE: "4100",
  DAMAGE_AND_FEES: "4200",
  DEPOSITS_FORFEITED: "4300",
  REFUNDS: "4900",
};

/**
 * Which revenue account an invoice line belongs to.
 *
 * Unknown kinds land in damage and fees rather than rental revenue: a
 * misfiled fee is a reporting annoyance, a fee misfiled as rental revenue
 * corrupts the one number the business is actually judged on.
 */
const ACCOUNT_FOR_KIND = {
  rental: ACCOUNTS.RENTAL_REVENUE,
  addon: ACCOUNTS.ADDON_REVENUE,
  damage: ACCOUNTS.DAMAGE_AND_FEES,
  fee: ACCOUNTS.DAMAGE_AND_FEES,
  cleaning: ACCOUNTS.DAMAGE_AND_FEES,
  late: ACCOUNTS.DAMAGE_AND_FEES,
  usage: ACCOUNTS.RENTAL_REVENUE,
  deposit_forfeit: ACCOUNTS.DEPOSITS_FORFEITED,
};

const accountForKind = (kind) => ACCOUNT_FOR_KIND[kind] || ACCOUNTS.DAMAGE_AND_FEES;

/** The calendar date an entry belongs to, formatted without timezone shift. */
const entryDateOf = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return [
    d.getUTCFullYear(),
    String(d.getUTCMonth() + 1).padStart(2, "0"),
    String(d.getUTCDate()).padStart(2, "0"),
  ].join("-");
};

const debit = (account, cents, extra = {}) => ({
  account_code: account, debit_cents: Math.round(cents), credit_cents: 0, ...extra,
});
const credit = (account, cents, extra = {}) => ({
  account_code: account, debit_cents: 0, credit_cents: Math.round(cents), ...extra,
});

/**
 * Writes a batch, having first checked it balances.
 *
 * The check is duplicated from the database on purpose. The trigger is the
 * guarantee; this is the error message -- it names the batch and the two
 * totals while the caller's context still exists, rather than surfacing a
 * constraint violation from a COMMIT three frames later.
 *
 * Zero-value legs are dropped before balancing. An invoice with no tax
 * configured would otherwise post a 0/0 row that the one-sided CHECK rejects.
 */
async function postBatch(operator, { journal, entryDate, lines, sourceTable, sourceId, customerId, memo }) {
  const kept = lines.filter((l) => (l.debit_cents || 0) + (l.credit_cents || 0) > 0);
  if (!kept.length) return createSuccessResponse({ posted: 0 });

  const debits = kept.reduce((s, l) => s + (l.debit_cents || 0), 0);
  const credits = kept.reduce((s, l) => s + (l.credit_cents || 0), 0);
  if (debits !== credits) {
    return createErrorResponse(
      `Ledger batch for ${journal} does not balance: debits ${debits}, credits ${credits}.`
    );
  }

  const batchId = crypto.randomUUID();
  const common = {
    operator_id: operator.id,
    batch_id: batchId,
    entry_date: entryDate,
    journal,
    currency: operator.currency || "USD",
    source_table: sourceTable || null,
    source_id: sourceId || null,
    customer_id: customerId || null,
    memo: memo || null,
  };

  for (const line of kept) {
    const result = await db.create("glEntries", { ...common, ...line });
    if (!result.success) return result;
  }

  return createSuccessResponse({ batch_id: batchId, posted: kept.length });
}

/**
 * An invoice: the customer now owes us, and we have earned something.
 *
 *   DR  Accounts receivable      the gross total
 *     CR  Revenue                per line, by kind
 *     CR  Sales tax payable      per rate, so a remittance is a query
 *
 * Tax is credited to a liability rather than revenue because it was never
 * ours -- it is collected on an authority's behalf and paid over later. An
 * operator whose ledger books tax as income will overpay income tax on money
 * they are holding for somebody else.
 */
export async function postInvoice(operator, invoice) {
  if (!operator?.id || !invoice?.id) return createErrorResponse("Nothing to post.");

  const lines = [];
  lines.push(debit(ACCOUNTS.RECEIVABLE, invoice.total_cents || 0));

  const byAccount = new Map();
  for (const line of invoice.line_items || []) {
    const account = accountForKind(line.kind);
    // net_cents where applyTax computed one -- on a tax-inclusive operator the
    // gross is not revenue, the net is, and the difference is the tax.
    const net = line.net_cents ?? line.amount_cents ?? 0;
    byAccount.set(account, (byAccount.get(account) || 0) + net);
  }
  for (const [account, cents] of byAccount) lines.push(credit(account, cents));

  for (const c of invoice.tax_breakdown?.components || []) {
    lines.push(
      credit(ACCOUNTS.TAX_PAYABLE, c.tax_cents || 0, {
        tax_rate_id: c.rate_id || null,
        memo: c.name,
      })
    );
  }

  return postBatch(operator, {
    journal: "invoice",
    entryDate: entryDateOf(invoice.issued_at || invoice.created_at),
    lines,
    sourceTable: "rms_invoices",
    sourceId: invoice.id,
    customerId: invoice.customer_id,
    memo: invoice.invoice_number,
  });
}

/**
 * A payment: cash arrives, the receivable shrinks.
 *
 *   DR  Undeposited funds
 *     CR  Accounts receivable
 *
 * No revenue is touched. That already happened when the invoice was raised,
 * and recognising it again here is how a cash-basis instinct double-counts a
 * year's income.
 *
 * A negative amount is a refund -- the schema already documents that
 * convention -- and posts the other way, against contra-revenue rather than
 * receivable, so a report can show gross revenue and refunds separately
 * instead of a net figure that hides both.
 */
export async function postPayment(operator, payment) {
  if (!operator?.id || !payment?.id) return createErrorResponse("Nothing to post.");

  const amount = payment.amount_cents || 0;
  if (amount === 0) return createSuccessResponse({ posted: 0 });

  const lines =
    amount > 0
      ? [debit(ACCOUNTS.UNDEPOSITED, amount), credit(ACCOUNTS.RECEIVABLE, amount)]
      : [debit(ACCOUNTS.REFUNDS, -amount), credit(ACCOUNTS.UNDEPOSITED, -amount)];

  return postBatch(operator, {
    journal: amount > 0 ? "payment" : "refund",
    entryDate: entryDateOf(payment.received_at || payment.created_at),
    lines,
    sourceTable: "rms_payments",
    sourceId: payment.id,
    customerId: payment.customer_id,
    memo: payment.reference || null,
  });
}

/**
 * A security deposit taken at checkout.
 *
 *   DR  Undeposited funds
 *     CR  Security deposits held      (a liability, not income)
 *
 * This is the posting that most rental bookkeeping gets wrong. The money is in
 * the till, so it looks like a good month; it is not earnings, it is a debt.
 */
export async function postDepositHeld(operator, { agreementId, customerId, amountCents, at }) {
  if (!amountCents) return createSuccessResponse({ posted: 0 });
  return postBatch(operator, {
    journal: "deposit",
    entryDate: entryDateOf(at),
    lines: [debit(ACCOUNTS.UNDEPOSITED, amountCents), credit(ACCOUNTS.DEPOSITS_HELD, amountCents)],
    sourceTable: "rms_agreements",
    sourceId: agreementId,
    customerId,
    memo: "Security deposit held",
  });
}

/**
 * A deposit resolved at check-in: applied, kept, returned, or a mix.
 *
 * The liability is cleared in full either way; the question is where the money
 * goes, and there are three genuinely different answers:
 *
 *   DR  Security deposits held           always, the whole amount held
 *     CR  Accounts receivable            applied against a damage invoice
 *     CR  Deposits forfeited             kept with NO invoice behind it
 *     CR  Undeposited funds              handed back
 *
 * The distinction between the first two credits is the one that matters, and
 * getting it wrong doubles revenue.
 *
 * When damage is charged, checkIn already puts it on the invoice as a `damage`
 * line, which books revenue at 4200 and raises a receivable. The deposit then
 * PAYS that receivable -- it does not earn anything, because the earning
 * already happened when the damage was billed. Crediting 4300 here as well
 * would record the same damage as income twice: once at 4200 and once at 4300,
 * against a single event.
 *
 * 4300 is for deposit money kept where nothing was ever invoiced -- a no-show
 * forfeit, a cancellation absorbed by the deposit. That is the only route by
 * which a deposit becomes income, and it is rarer than it looks.
 */
export async function postDepositResolved(
  operator,
  { agreementId, customerId, heldCents, appliedToInvoiceCents = 0, forfeitedToRevenueCents = 0, at }
) {
  if (!heldCents) return createSuccessResponse({ posted: 0 });

  const applied = Math.min(Math.max(appliedToInvoiceCents, 0), heldCents);
  const forfeited = Math.min(Math.max(forfeitedToRevenueCents, 0), heldCents - applied);
  const returned = heldCents - applied - forfeited;

  const lines = [debit(ACCOUNTS.DEPOSITS_HELD, heldCents)];
  if (applied) lines.push(credit(ACCOUNTS.RECEIVABLE, applied));
  if (forfeited) lines.push(credit(ACCOUNTS.DEPOSITS_FORFEITED, forfeited));
  if (returned) lines.push(credit(ACCOUNTS.UNDEPOSITED, returned));

  const memo = applied
    ? returned
      ? "Deposit applied to charges, balance returned"
      : "Deposit applied to charges"
    : forfeited
    ? "Deposit forfeited"
    : "Deposit returned";

  return postBatch(operator, {
    journal: "deposit",
    entryDate: entryDateOf(at),
    lines,
    sourceTable: "rms_agreements",
    sourceId: agreementId,
    customerId,
    memo,
  });
}

export default {
  ACCOUNTS,
  postInvoice,
  postPayment,
  postDepositHeld,
  postDepositResolved,
};
